import { useCallback, useEffect, useRef, useState } from 'react'
import SeoHead from '../components/SeoHead'
import { Link } from 'react-router-dom'
import {
  api, ApiError, base64ToBlob, bridge, fileToBase64, triggerDownload,
  type BridgeCertInfo, type BridgeDriverInfo, type BridgeStatus,
} from '../lib/api'
import type { ExtractedPfx } from '../lib/pfx'
import { SITE } from '../lib/site'
import SecretInput from '../components/SecretInput'

type Mode = 'token' | 'pfx'

export default function HashSignPage() {
  const [mode, setMode] = useState<Mode>('token')

  return (
    <>
      <SeoHead
        title="Elektronik imza atma — USB token ile nitelikli e-imza"
        description="Nitelikli elektronik imza kartınızla (AKİS, SafeNet, Gemalto) tarayıcıdan 5070 sayılı Kanun'a uygun imza atın; dilerseniz PFX ile gelişmiş imza üretin. Özel anahtarınız kartın dışına çıkmaz."
        path="/hash-sign"
      />
      <section className="container-page py-12 md:py-16">
        <h1 className="text-3xl md:text-4xl font-semibold">Elektronik İmza</h1>
        <p className="mt-2 text-[var(--color-fg-muted)] max-w-3xl">
          USB token ile nitelikli elektronik imza (5070 uyumlu) veya PFX ile gelişmiş imza üret.
          Her iki yolda da private key tarayıcıdan çıkmaz.
        </p>

        <div className="mt-6 inline-flex rounded-md border border-[var(--color-border)] bg-[var(--color-bg-soft)] p-1">
          <ModeButton active={mode === 'token'} onClick={() => setMode('token')}>
            USB Token (Önerilen)
          </ModeButton>
          <ModeButton active={mode === 'pfx'} onClick={() => setMode('pfx')}>
            PFX / Gelişmiş İmza
          </ModeButton>
        </div>

        <div className="mt-6">
          {mode === 'token' ? <TokenFlow /> : <PfxFlow />}
        </div>
      </section>
    </>
  )
}

function ModeButton({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      type="button"
      className={`rounded px-4 py-1.5 text-sm font-medium transition-colors ${
        active
          ? 'bg-white text-[var(--color-fg)] shadow-sm'
          : 'text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]'
      }`}
    >
      {children}
    </button>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// TOKEN FLOW (W.Sign Desktop bridge)
// ──────────────────────────────────────────────────────────────────────────────

type TokenStatus =
  | 'probing'
  | 'no-bridge'
  | 'bridge-ready'        // bridge OK, no driver loaded yet
  | 'tokens-loaded'
  | 'pin-entry'
  | 'cert-list'
  | 'signing'
  | 'success'
  | 'error'

interface TokenSelection {
  driverPath: string
  driverName: string
  slotId:     number
  label:      string
  serial:     string
}

interface SignedResult {
  blob:          Blob
  outputName:    string
  size:          number
  signerSubject: string
}

function TokenFlow() {
  const [status,       setStatus]      = useState<TokenStatus>('probing')
  const [bridgeInfo,   setBridgeInfo]  = useState<BridgeStatus | null>(null)
  const [drivers,      setDrivers]     = useState<BridgeDriverInfo[]>([])
  const [selectedTok,  setSelectedTok] = useState<TokenSelection | null>(null)
  const [pin,          setPin]         = useState('')
  const [certs,        setCerts]       = useState<BridgeCertInfo[]>([])
  const [selectedCert, setSelectedCert] = useState<BridgeCertInfo | null>(null)
  const [contentFile,  setContentFile] = useState<File | null>(null)
  const [detached,     setDetached]    = useState(false)  // false = attached (.imz), tek dosya
  const [withTimestamp, setWithTimestamp] = useState(false)
  const [error,        setError]       = useState<string | null>(null)
  const [progress,     setProgress]    = useState('')
  const [result,       setResult]      = useState<SignedResult | null>(null)
  const [waking,       setWaking]      = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // Bridge probe on mount + retry button
  const probeBridge = useCallback(async () => {
    setStatus('probing')
    setError(null)
    try {
      const st = await bridge.probe()
      if (st === null) {
        setStatus('no-bridge')
        return
      }
      setBridgeInfo(st)
      setStatus('bridge-ready')
      // Auto-load tokens
      try {
        const tokens = await bridge.listTokens()
        setDrivers(tokens)
        setStatus('tokens-loaded')
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err))
        setStatus('error')
      }
    } catch (err) {
      setStatus('no-bridge')
    }
  }, [])

  useEffect(() => { void probeBridge() }, [probeBridge])

  // "Uygulamayı başlat": kurulu ama çalışmayan köprüyü wsign://wake ile uyandır,
  // ayağa kalkınca normal probe akışına dön.
  const wake = useCallback(async () => {
    setWaking(true); setError(null)
    try {
      const st = await bridge.wakeAndWait()
      if (st) { await probeBridge(); return }
      setError('W.Sign köprüsü başlatılamadı. Kurulu değilse aşağıdan indirin; kuruluysa elle açıp "Tekrar dene"ye basın.')
    } finally {
      setWaking(false)
    }
  }, [probeBridge])

  const selectToken = (driver: BridgeDriverInfo, tok: BridgeDriverInfo['tokens'][number]) => {
    setSelectedTok({
      driverPath: driver.path,
      driverName: driver.driver,
      slotId:     tok.slotId,
      label:      tok.label,
      serial:     tok.serial,
    })
    setPin('')
    setCerts([])
    setSelectedCert(null)
    setStatus('pin-entry')
    setError(null)
  }

  const submitPin = useCallback(async () => {
    if (!selectedTok || !pin) return
    setStatus('signing')  // re-use spinner UI
    setProgress("Sertifikalar yükleniyor… (token'da PIN doğrulanıyor)")
    setError(null)
    try {
      const list = await bridge.listCertificates({
        driverPath: selectedTok.driverPath,
        slotId:     selectedTok.slotId,
        pin,
      })
      setCerts(list)
      setStatus('cert-list')
      setProgress('')
    } catch (err) {
      setStatus('pin-entry')
      setError(err instanceof ApiError ? `${err.code}: ${err.message}` : err instanceof Error ? err.message : String(err))
      setProgress('')
    }
  }, [selectedTok, pin])

  const signNow = useCallback(async () => {
    if (!selectedTok || !selectedCert || !contentFile || !pin) return
    setStatus('signing')
    setProgress('Dosya hazırlanıyor…')
    setError(null)
    try {
      const contentB64 = await fileToBase64(contentFile)
      setProgress('Token imzalıyor… (PIN doğrulandı)')
      const res = await bridge.sign({
        driverPath:    selectedTok.driverPath,
        slotId:        selectedTok.slotId,
        pin,
        ckaId:         selectedCert.ckaId,
        contentBase64: contentB64,
        algorithm:     'CAdES-BES',
        detached,
      })

      let finalBytes  = res.signedBytes
      let formatLabel = 'CAdES-BES'

      // Optional: ask server to attach a TSA-issued signature-time-stamp.
      if (withTimestamp) {
        setProgress('Zaman damgası ekleniyor (TSA çağrısı)…')
        const tsResp = await api.addTimestamp({
          cadesBesBase64: res.signedBytes,
        })
        finalBytes = tsResp.cadesT
        formatLabel = tsResp.format  // "CAdES-T"
      }

      const blob = base64ToBlob(finalBytes, 'application/pkcs7-mime')
      setResult({
        blob,
        outputName:    baseName(contentFile.name) + (detached ? '.p7s' : '.imz'),
        size:          blob.size,
        signerSubject: `${res.signerSubject} (${formatLabel})`,
      })
      setStatus('success')
      setProgress('')
    } catch (err) {
      setStatus('cert-list')
      setError(err instanceof ApiError ? `${err.code}: ${err.message}` : err instanceof Error ? err.message : String(err))
      setProgress('')
    }
  }, [selectedTok, selectedCert, contentFile, pin, detached, withTimestamp])

  // ── Render branches ────────────────────────────────────────────────────────

  if (status === 'probing') {
    return <div className="card"><p className="text-sm text-[var(--color-fg-muted)]">Yerel W.Sign köprüsü aranıyor…</p></div>
  }

  if (status === 'no-bridge') {
    return (
      <div className="card border-blue-200 bg-blue-50">
        <h2 className="text-lg font-semibold text-blue-900">W.Sign yardımcı uygulaması gerekli</h2>
        <p className="mt-2 text-sm text-blue-800 max-w-2xl">
          Tarayıcı USB token'a doğrudan erişemez (güvenlik kısıtı); imzalama yerel W.Sign uygulaması
          üzerinden yapılır. <strong>Uygulama zaten kuruluysa "Uygulamayı başlat"a basın</strong> —
          kurulu değilse aşağıdan indirin. W.Sign arka planda, yalnızca 127.0.0.1 üzerinde dinler;
          AKİS, SafeNet, Gemalto, Bit4id, ACS tokenlarını (32-bit + 64-bit) destekler.
        </p>
        <p className="mt-2 text-xs text-blue-700 max-w-2xl">
          İlk başlatmada tarayıcınız W.Sign'ı açmak için izin isteyebilir — "Aç"a basın.
          İlk kurulumda SmartScreen uyarısı çıkarsa "Daha fazla bilgi → Yine de çalıştır" deyin.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button onClick={wake} disabled={waking} type="button" className="btn-primary">
            {waking ? 'Başlatılıyor…' : 'Uygulamayı başlat'}
          </button>
          <a href={SITE.downloadUrl} className="btn-ghost">
            W.Sign kur (Windows) →
          </a>
          <button onClick={probeBridge} disabled={waking} type="button" className="btn-ghost">
            Tekrar dene
          </button>
        </div>
        <p className="mt-4 text-xs text-blue-700">
          Token'ınız yoksa veya başka bir sebepten köprüyü kullanamıyorsanız
          <strong> PFX Dosyası</strong> sekmesine geçin.
        </p>
        {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Bridge connection banner — end-user surface: reassuring, not technical.
          The base URL + version stay in the title tooltip for support. */}
      <div className="card border-emerald-200 bg-emerald-50">
        <p className="text-sm text-emerald-900" title={`${bridgeInfo?.bridgeBaseUrl} · v${bridgeInfo?.version}`}>
          <strong>W.Sign masaüstü uygulaması bağlı.</strong> Kartınızla güvenle imzalayabilirsiniz.
        </p>
      </div>

      {/* Step 1: token select */}
      {(status === 'tokens-loaded' || status === 'pin-entry' || status === 'cert-list' ||
        status === 'signing'      || status === 'success') && (
        <div className="card">
          <h2 className="text-lg font-semibold">1. Token Seç</h2>
          {drivers.length === 0 && (
            <p className="mt-3 text-sm text-amber-700">
              Yüklü PKCS#11 sürücüsü yok veya hiçbir tokende kart takılı değil. AKİS / SafeNet / Gemalto sürücüsü
              kuruluysa token'ı USB'ye taktığından emin ol; sonra <button onClick={probeBridge} className="underline">tekrar tara</button>.
            </p>
          )}
          {drivers.map((d, idx) => (
            <div key={idx} className="mt-3 rounded border border-[var(--color-border)] p-3">
              <div className="flex items-start justify-between">
                <div>
                  {/* Driver name is enough for the end user; the DLL path is support
                      detail, kept in the tooltip rather than shown as mono noise. */}
                  <p className="font-medium" title={d.path}>{d.driver}</p>
                </div>
                {!d.available && <span className="badge badge-warn">erişilemiyor</span>}
              </div>
              {d.error && <p className="mt-1 text-xs text-red-700">{d.error}</p>}
              {d.tokens.length > 0 && (
                <div className="mt-3 space-y-2">
                  {d.tokens.map((t, ti) => {
                    const isSel = selectedTok?.driverPath === d.path && selectedTok?.slotId === t.slotId
                    return (
                      <button
                        key={ti}
                        onClick={() => selectToken(d, t)}
                        type="button"
                        className={`block w-full rounded border p-3 text-left text-sm transition-colors ${
                          isSel
                            ? 'border-[var(--color-accent)] bg-[var(--color-accent-wash)]'
                            : 'border-[var(--color-border)] hover:bg-[var(--color-bg-soft)]'
                        }`}
                      >
                        <p className="font-medium">{t.label || '(etiketsiz token)'}</p>
                        <p className="text-xs text-[var(--color-fg-muted)] mt-0.5">
                          {t.manufacturer} {t.model} · seri: <code className="font-mono">{t.serial}</code> · slot: {t.slotId}
                        </p>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Step 2: PIN */}
      {selectedTok && (status === 'pin-entry' || status === 'cert-list' || status === 'signing' || status === 'success') && (
        <div className="card">
          <h2 className="text-lg font-semibold">2. Token PIN'i</h2>
          <p className="mt-1 text-xs text-[var(--color-fg-muted)]">
            Seçili: <strong>{selectedTok.label}</strong> ({selectedTok.driverName})
          </p>
          {status === 'pin-entry' ? (
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end">
              <label className="flex-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">
                  PIN
                </span>
                <SecretInput
                  value={pin}
                  onChange={e => setPin(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') void submitPin() }}
                  placeholder="••••"
                  autoFocus
                  className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-white px-3 py-2 text-sm focus:border-[var(--color-accent)] focus:outline-none"
                />
              </label>
              <button onClick={submitPin} disabled={!pin} type="button" className="btn-primary">
                Sertifikaları getir
              </button>
            </div>
          ) : (
            <p className="mt-2 text-sm text-emerald-700">PIN doğrulandı · {certs.length} sertifika yüklü</p>
          )}
        </div>
      )}

      {/* Step 3: cert select */}
      {(status === 'cert-list' || status === 'signing' || status === 'success') && certs.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold">3. Sertifika</h2>
          <div className="mt-3 space-y-2">
            {certs.map((c, idx) => {
              const isSel = selectedCert?.ckaId === c.ckaId
              return (
                <button
                  key={idx}
                  onClick={() => setSelectedCert(c)}
                  type="button"
                  className={`block w-full rounded border p-3 text-left text-sm transition-colors ${
                    isSel
                      ? 'border-[var(--color-accent)] bg-[var(--color-accent-wash)]'
                      : 'border-[var(--color-border)] hover:bg-[var(--color-bg-soft)]'
                  }`}
                >
                  <p className="font-medium">{c.subject}</p>
                  <p className="mt-0.5 text-xs text-[var(--color-fg-muted)]">İhraç: {c.issuer}</p>
                  <p className="mt-0.5 text-xs font-mono text-[var(--color-fg-dim)]">
                    {new Date(c.notBefore).toLocaleDateString('tr-TR')} → {new Date(c.notAfter).toLocaleDateString('tr-TR')}
                    {!c.hasPrivateKey && ' · (private key yok)'}
                  </p>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Step 4: file + sign */}
      {selectedCert && (status === 'cert-list' || status === 'signing' || status === 'success') && (
        <div className="card">
          <h2 className="text-lg font-semibold">4. İmzalanacak dosya</h2>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              onClick={() => fileInputRef.current?.click()}
              type="button"
              className="btn-ghost"
              disabled={status === 'signing'}
            >
              Dosya seç
            </button>
            <span className="text-sm font-mono text-[var(--color-fg-dim)]">
              {contentFile?.name ?? '(seçili dosya yok)'}
              {contentFile && <> · {formatBytes(contentFile.size)}</>}
            </span>
            <input
              ref={fileInputRef}
              type="file"
              onChange={e => { const f = e.target.files?.[0]; if (f) setContentFile(f) }}
              className="hidden"
            />
          </div>

          {contentFile && (
            <>
              <div className="mt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">
                  İmza tipi
                </p>
                <div className="mt-2 space-y-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={!detached}
                      onChange={() => setDetached(false)}
                      disabled={status === 'signing'}
                      className="mt-0.5 accent-[var(--color-accent)]"
                    />
                    <span className="text-sm">
                      <strong>Tek dosya (.imz)</strong> — içerik + imza birlikte; TÜBİTAK İmzager uyumlu
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={detached}
                      onChange={() => setDetached(true)}
                      disabled={status === 'signing'}
                      className="mt-0.5 accent-[var(--color-accent)]"
                    />
                    <span className="text-sm">
                      <strong>Ayrı imza (.p7s)</strong> — orijinal dosya ayrı kalır; doğrulayıcı her ikisini ister
                    </span>
                  </label>
                </div>
              </div>

              <div className="mt-4 rounded border border-[var(--color-border)] bg-[var(--color-bg-soft)] p-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={withTimestamp}
                    onChange={e => setWithTimestamp(e.target.checked)}
                    disabled={status === 'signing'}
                    className="mt-0.5 accent-[var(--color-accent)]"
                  />
                  <span className="text-sm">
                    <strong>Zaman damgası ekle (CAdES-T)</strong> — sunucu RFC 3161 TSA çağırır,
                    <code className="font-mono"> signature-time-stamp</code> ekler. İmzanın
                    "ne zaman atıldığı" üçüncü taraf TSA tarafından kanıtlanır.
                  </span>
                </label>
              </div>

              <div className="mt-4 flex items-center gap-3">
                <button
                  onClick={signNow}
                  type="button"
                  disabled={status === 'signing'}
                  className="btn-primary"
                >
                  {status === 'signing'
                    ? 'İmzalanıyor…'
                    : `İmzala (CAdES-BES ${detached ? '.p7s' : '.imz'}) ve indir`}
                </button>
                {progress && <span className="text-xs text-[var(--color-fg-muted)]">{progress}</span>}
              </div>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="card border-red-200 bg-red-50">
          <h3 className="font-semibold text-red-800">Hata</h3>
          <p className="mt-1 text-sm text-red-700">{error}</p>
        </div>
      )}

      {status === 'success' && result && (
        <div className="card border-emerald-200 bg-emerald-50">
          <h3 className="text-lg font-semibold text-emerald-900">İmza üretildi</h3>
          <p className="mt-1 text-sm text-emerald-800">
            <strong>{result.signerSubject}</strong> ile imzalandı.
          </p>
          <p className="mt-1 text-xs text-emerald-700">
            Çıktı: <code className="font-mono">{result.outputName}</code> · {formatBytes(result.size)}
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              onClick={() => triggerDownload(result.blob, result.outputName)}
              type="button"
              className="btn-primary"
            >
              İndir
            </button>
            <Link to="/verify" className="btn-ghost">Doğrulayıcıda aç →</Link>
          </div>
        </div>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// PFX FLOW (fallback — no token needed, browser-side)
// ──────────────────────────────────────────────────────────────────────────────

type PfxStatus = 'idle' | 'parsing' | 'ready' | 'reading' | 'preparing' | 'signing-local' | 'completing' | 'success' | 'error'

function PfxFlow() {
  const [pfx,           setPfx]           = useState<ExtractedPfx | null>(null)
  const [pfxFileName,   setPfxFileName]   = useState('')
  const [password,      setPassword]      = useState('')
  const [pfxBuffer,     setPfxBuffer]     = useState<Uint8Array | null>(null)
  const [contentFile,   setContentFile]   = useState<File | null>(null)
  const [status,        setStatus]        = useState<PfxStatus>('idle')
  const [error,         setError]         = useState<string | null>(null)
  const [result,        setResult]        = useState<SignedResult | null>(null)
  const [progressNote,  setProgressNote]  = useState('')
  const pfxInputRef     = useRef<HTMLInputElement | null>(null)
  const contentInputRef = useRef<HTMLInputElement | null>(null)

  const reset = () => {
    setPfx(null); setPfxFileName(''); setPassword(''); setPfxBuffer(null)
    setContentFile(null); setStatus('idle'); setError(null); setResult(null); setProgressNote('')
  }

  const onPfxSelected = useCallback(async (file: File) => {
    setPfxFileName(file.name)
    setPfx(null); setError(null)
    setPfxBuffer(new Uint8Array(await file.arrayBuffer()))
  }, [])

  const onUnlockPfx = useCallback(async () => {
    if (!pfxBuffer || !password) return
    setStatus('parsing'); setError(null)
    try {
      const { parsePfx } = await import('../lib/pfx')
      setPfx(await parsePfx(pfxBuffer, password))
      setStatus('ready')
    } catch (err) {
      setStatus('error')
      setError(err instanceof Error ? err.message : String(err))
    }
  }, [pfxBuffer, password])

  const onSign = useCallback(async () => {
    if (!pfx || !contentFile) return
    setStatus('reading'); setError(null); setResult(null); setProgressNote('Dosya okunuyor…')
    try {
      const contentB64 = await fileToBase64(contentFile)
      const { bytesToBase64, signPkcs1Sha256 } = await import('../lib/pfx')
      const certB64 = bytesToBase64(pfx.certificateDer)

      setStatus('preparing'); setProgressNote('Sunucu CMS gövdesini hazırlıyor…')
      const prep = await api.hashSignPrepare({
        contentBase64:        contentB64,
        signerCertificateB64: certB64,
        algorithm:            'CAdES-BES',
      })

      setStatus('signing-local'); setProgressNote('Yerel olarak imzalanıyor (private key tarayıcıdan çıkmaz)…')
      const dataToSign = base64ToUint8(prep.dataToSign)
      const rawSig     = await signPkcs1Sha256(pfx.privateKey, dataToSign)
      const rawSigB64  = bytesToBase64(rawSig)

      setStatus('completing'); setProgressNote('Sunucu CMS gövdesini tamamlıyor…')
      const done = await api.hashSignComplete({
        sessionId:       prep.sessionId,
        rawSignatureB64: rawSigB64,
      })

      const blob = base64ToBlob(done.signedCms, 'application/pkcs7-signature')
      setResult({
        blob,
        outputName:    baseName(contentFile.name) + '.p7s',
        size:          blob.size,
        signerSubject: pfx.subject,
      })
      setStatus('success'); setProgressNote('')
    } catch (err) {
      setStatus('error'); setProgressNote('')
      setError(err instanceof ApiError ? `${err.code}: ${err.message}` : err instanceof Error ? err.message : String(err))
    }
  }, [pfx, contentFile])

  const busy = ['parsing', 'reading', 'preparing', 'signing-local', 'completing'].includes(status)

  return (
    <div className="space-y-6">
      <div className="card border-amber-200 bg-amber-50">
        <p className="text-sm text-amber-800">
          Bu yöntem <strong>5070 sayılı Kanun kapsamında nitelikli (güvenli) elektronik imza üretmez</strong> —
          ıslak imza eşdeğeri değildir. Kamu kurumları, mahkemeler ve e-devlet sistemleri kabul etmez.
          Yasal geçerlilik için USB token kullanın. PFX, geliştirici testi/entegrasyon için uygundur.
          Şifre ve private key sunucuya gitmez.
        </p>
        <p className="mt-2 text-xs text-amber-700">Demo modu: IP başına saatte 5 imza.</p>
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold">1. Sertifika (PFX / P12)</h2>
        {!pfx ? (
          <>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
              <button onClick={() => pfxInputRef.current?.click()} className="btn-ghost" type="button" disabled={busy}>
                PFX seç
              </button>
              <span className="text-sm font-mono text-[var(--color-fg-dim)]">
                {pfxFileName || '(seçili dosya yok)'}
              </span>
              <input ref={pfxInputRef} type="file" accept=".pfx,.p12"
                     onChange={e => { const f = e.target.files?.[0]; if (f) void onPfxSelected(f) }}
                     className="hidden" />
            </div>

            {pfxBuffer && (
              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end">
                <label className="flex-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">PFX şifresi</span>
                  <SecretInput value={password} onChange={e => setPassword(e.target.value)}
                         onKeyDown={e => { if (e.key === 'Enter') void onUnlockPfx() }}
                         placeholder="••••••••" autoFocus
                         className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-white px-3 py-2 text-sm focus:border-[var(--color-accent)] focus:outline-none" />
                </label>
                <button onClick={onUnlockPfx} disabled={busy || !password} className="btn-primary" type="button">
                  {status === 'parsing' ? 'Açılıyor…' : 'Sertifikayı yükle'}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="mt-4 rounded border border-emerald-200 bg-emerald-50 p-3 text-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-medium">{pfx.subject}</p>
                <p className="mt-1 text-xs text-emerald-700">İhraç: {pfx.issuer}</p>
                <p className="mt-1 text-xs text-emerald-700">
                  {pfx.notBefore.toLocaleDateString('tr-TR')} → {pfx.notAfter.toLocaleDateString('tr-TR')}
                </p>
              </div>
              <button onClick={reset} className="btn-ghost text-xs" type="button">Sıfırla</button>
            </div>
          </div>
        )}
      </div>

      {pfx && (
        <div className="card">
          <h2 className="text-lg font-semibold">2. İmzalanacak dosya</h2>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
            <button onClick={() => contentInputRef.current?.click()} className="btn-ghost" type="button" disabled={busy}>
              Dosya seç
            </button>
            <span className="text-sm font-mono text-[var(--color-fg-dim)]">
              {contentFile?.name ?? '(seçili dosya yok)'}
              {contentFile && <> · {formatBytes(contentFile.size)}</>}
            </span>
            <input ref={contentInputRef} type="file"
                   onChange={e => { const f = e.target.files?.[0]; if (f) setContentFile(f) }}
                   className="hidden" />
          </div>
          {contentFile && (
            <div className="mt-4 flex items-center gap-3">
              <button onClick={onSign} disabled={busy} className="btn-primary" type="button">
                {busy ? 'İşleniyor…' : 'İmzala (CAdES-BES detached) ve indir'}
              </button>
              {progressNote && <span className="text-xs text-[var(--color-fg-muted)]">{progressNote}</span>}
            </div>
          )}
        </div>
      )}

      {status === 'error' && (
        <div className="card border-red-200 bg-red-50">
          <h3 className="font-semibold text-red-800">Hata</h3>
          <p className="mt-1 text-sm text-red-700">{error}</p>
          <button onClick={reset} type="button" className="btn-ghost mt-3 text-xs">Sıfırla</button>
        </div>
      )}

      {status === 'success' && result && (
        <div className="card border-emerald-200 bg-emerald-50">
          <h3 className="text-lg font-semibold text-emerald-900">İmza üretildi</h3>
          <p className="mt-1 text-sm text-emerald-800"><strong>{result.signerSubject}</strong> ile imzalandı.</p>
          <p className="mt-1 text-xs text-emerald-700">
            Çıktı: <code className="font-mono">{result.outputName}</code> · {formatBytes(result.size)}
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button onClick={() => triggerDownload(result.blob, result.outputName)} className="btn-primary" type="button">
              İndir
            </button>
            <Link to="/verify" className="btn-ghost">Doğrulayıcıda aç →</Link>
            <button onClick={reset} className="btn-ghost" type="button">Yeni imza</button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function baseName(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot > 0 ? name.substring(0, dot) : name
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`
}

function base64ToUint8(b64: string): Uint8Array {
  const binary = atob(b64)
  const bytes  = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}
